import { ReactNode } from 'react';
import { EndReasonTypeDataProvider } from '../contexts/EndReasonTypeDataContext';
import { FafsaDataProvider } from '../contexts';
import { ServiceDataProvider } from '../contexts/ServiceDataContext';
import { ParticipantsDataProvider } from '../contexts/ParticipantsDataContext';
import { GenderDataProvider } from '../contexts/GenderDataContext';
import { EligibilityDataProvider } from '../contexts/EligibilityDataContext';
import { LkDashboardCardId } from '../types';

interface DashboardDataProvidersProps {
    children: ReactNode;
    visibleCardIds: number[];
}

const FAFSA_IDS = [LkDashboardCardId.FafsaChart, LkDashboardCardId.FafsaTable];
const END_REASON_IDS = [LkDashboardCardId.EndReasonTypeChart, LkDashboardCardId.EndReasonTypeTable];
const SERVICE_IDS = [LkDashboardCardId.ServiceChart, LkDashboardCardId.ServiceTable];
const PARTICIPANTS_IDS = [LkDashboardCardId.ParticipantsChart, LkDashboardCardId.ParticipantsTable];
const GENDER_IDS = [LkDashboardCardId.GenderChart, LkDashboardCardId.GenderTable];
const ELIGIBILITY_IDS = [LkDashboardCardId.EligibilityChart, LkDashboardCardId.EligibilityTable];

/**
 * Providers are only mounted when at least one of their dependent cards is visible.
 * Cards that are self-contained (Notifications, TotalStudents, SchoolEnrollment)
 * maintain their own internal providers.
 */
export const DashboardDataProviders = ({ children, visibleCardIds }: DashboardDataProvidersProps) => {
    const needsFafsa = visibleCardIds.some((id) => FAFSA_IDS.includes(id));
    const needsEndReason = visibleCardIds.some((id) => END_REASON_IDS.includes(id));
    const needsService = visibleCardIds.some((id) => SERVICE_IDS.includes(id));
    const needsParticipants = visibleCardIds.some((id) => PARTICIPANTS_IDS.includes(id));
    const needsGender = visibleCardIds.some((id) => GENDER_IDS.includes(id));
    const needsEligibility = visibleCardIds.some((id) => ELIGIBILITY_IDS.includes(id));

    let wrapped = children;

    if (needsFafsa) {
        wrapped = <FafsaDataProvider>{wrapped}</FafsaDataProvider>;
    }
    if (needsEndReason) {
        wrapped = <EndReasonTypeDataProvider>{wrapped}</EndReasonTypeDataProvider>;
    }
    if (needsParticipants) {
        wrapped = <ParticipantsDataProvider>{wrapped}</ParticipantsDataProvider>;
    }
    if (needsService) {
        wrapped = <ServiceDataProvider>{wrapped}</ServiceDataProvider>;
    }
    if (needsGender) {
        wrapped = <GenderDataProvider>{wrapped}</GenderDataProvider>;
    }
    if (needsEligibility) wrapped = <EligibilityDataProvider>{wrapped}</EligibilityDataProvider>;

    return <>{wrapped}</>;
};